import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Spinner } from "reactstrap";
import { url } from "../../store/initialStatus";

/**
 * CIB Hosted Checkout (MPGS API v100).
 *
 * On mount:
 *   1. POSTs `payload` to the flow's createsession.php, which opens a gateway
 *      session and returns {session, order, checkoutScript}.
 *   2. Loads checkout.js from `checkoutScript`. The gateway host lives only in
 *      backend/include/env.php, so it is never hard-coded here.
 *   3. Redirects the payer to the gateway-hosted payment page.
 *
 * The page is left on success: the gateway returns the payer to
 * backend/payment/paymentreturn.php, which settles the payment and shows the
 * receipt. `onFinish(null)` is only called when the redirect never happens
 * (session error, checkout.js error or the payer cancelling).
 */

const SCRIPT_ID = "mpgs-checkout-js";

declare global {
  interface Window {
    Checkout: any;
    mpgsError?: (error: any) => void;
    mpgsCancel?: () => void;
  }
}

interface Props {
  /** createsession.php path, relative to REACT_APP_BASEURL. */
  endpoint: string;
  /** Body sent to createsession.php. */
  payload: object;
  onFinish: (result: any) => void;
}

/** Loads checkout.js once; checkout.js reads its callbacks from data-* attributes. */
function loadCheckoutScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.src = src;
    script.setAttribute("data-error", "mpgsError");
    script.setAttribute("data-cancel", "mpgsCancel");
    script.onload = () => resolve();
    script.onerror = () => {
      script.remove();
      reject(new Error("Could not load the payment page."));
    };
    document.body.appendChild(script);
  });
}

export default function HostedCheckout({ endpoint, payload, onFinish }: Props) {
  // True while preparing (request > script > configure); showPaymentPage()
  // brings its own loading indicator, so ours hides before calling it.
  const [preparing, setPreparing] = useState(true);

  /** Shows the error, then hands control back to the caller. */
  const fail = useCallback(
    (message: string) => {
      Swal.fire({ title: "Error", icon: "error", text: message }).then(() =>
        onFinish(null),
      );
    },
    [onFinish],
  );

  // checkout.js callbacks (data-error / data-cancel). Only fire before the
  // redirect - a completed payment leaves this page.
  const onCheckoutError = useCallback(
    (error: any) => {
      console.error("Hosted Checkout error:", error);
      fail("Please refresh the page and try again.");
    },
    [fail],
  );

  const onCheckoutCancel = useCallback(() => {
    fail("Payment cancelled.");
  }, [fail]);

  useEffect(() => {
    window.mpgsError = onCheckoutError;
    window.mpgsCancel = onCheckoutCancel;

    axios
      .post(url + endpoint, payload)
      .then(async (res: any) => {
        const { session, checkoutScript } = res.data.data;
        await loadCheckoutScript(checkoutScript);
        await window.Checkout.configure({ session: { id: session } });
        setPreparing(false);
      })
      .catch((err: any) => {
        fail(
          err?.response?.data?.msg ||
            "Could not open the payment page. Please try again.",
        );
      });
    // Runs once per mount: a new payment mounts a new component.
  }, [endpoint, payload, fail, onCheckoutError, onCheckoutCancel]);

  useEffect(() => {
    if (!preparing) {
      window.Checkout.showPaymentPage();
    }
  }, [preparing]);
  
  // Shown until the gateway page takes over
  return (
    preparing && (
      <div
        className="flex-grow-1 d-flex flex-column justify-content-center align-items-center text-center gap-3"
        role="status"
        aria-live="polite"
      >
        <Spinner style={{ width: "4rem", height: "4rem", color: "#03589b" }} />
        <h2 className="h3 fw-semibold mb-0" style={{ color: "#1f2937" }}>
          Redirecting to the secure payment page...
        </h2>
        <p className="text-muted mb-0 d-flex align-items-center gap-2">
          <i className="fas fa-lock" aria-hidden="true"></i>
          Please don't close or refresh this page.
        </p>
      </div>
    )
  );
}
